import { useEffect, useRef, useCallback } from 'react';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { S3MultipartSigner } from './S3MultipartSigner';
import { UploadApi } from './UploadApi';
import { UploadUtils } from './UploadUtils';
import { useUploadStore } from './uploadStore';
import { ensureValidCredentials } from './useUploadCredentials';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import {
  VIDEO_EXTENSIONS,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_CONCURRENT_FILE_UPLOADS,
  MULTIPART_MIN_PART_SIZE,
  UPLOAD_RETRY_DELAYS,
  PROGRESS_REPORT_THROTTLE_MS,
} from '@/lib/constants/upload';
import type { IngestcfgData } from '@/lib/types/Ingest';

/**
 * Core hook: manages Uppy instance with @uppy/aws-s3 multipart plugin.
 *
 * Wires all Uppy callbacks to Zustand store, S3MultipartSigner, and UploadApi.
 * Uppy instance is stored in useRef — NOT in Zustand (avoids serialization issues).
 *
 * Lifecycle: recreated when selectedCId changes (cleanup cancels all + destroys).
 */
export function useUppy(cId: number | null, ingestcfg: IngestcfgData | null) {
  const uppyRef = useRef<Uppy | null>(null);
  const queryClient = useQueryClient();

  // Per-file progress report throttle timestamps (in hook scope, not Zustand)
  const lastReportTSRef = useRef<Map<string, number>>(new Map());

  const createUppyInstance = useCallback(() => {
    if (cId == null) return null;

    // Capture cId for closures — prevents stale reference if cId changes
    const capturedCId = cId;

    const uppy = new Uppy({
      id: 'video-upload',
      autoProceed: true,
      restrictions: {
        allowedFileTypes: [...VIDEO_EXTENSIONS],
        maxFileSize: MAX_UPLOAD_SIZE_BYTES,
      },
    });

    uppy.use(AwsS3, {
      shouldUseMultipart: true as const,
      getChunkSize: (file) =>
        Math.max(MULTIPART_MIN_PART_SIZE, Math.ceil(file.size / 10_000)),
      limit: MAX_CONCURRENT_FILE_UPLOADS,
      retryDelays: [...UPLOAD_RETRY_DELAYS],

      // --- Adapter layer: Uppy callbacks → S3MultipartSigner + UploadApi ---

      async createMultipartUpload(file) {
        const credentials = await ensureValidCredentials(capturedCId);

        // Find the UploadItem by uploadItemId meta or Uppy file ID
        const { items } = useUploadStore.getState();
        const uploadItemId = file.meta?.uploadItemId as string | undefined;
        const item = uploadItemId
          ? items.find((i) => i.id === uploadItemId)
          : items.find((i) => i.uppyFileId === file.id);

        if (!item) {
          throw new Error(`No UploadItem found for Uppy file ${file.id}`);
        }

        // Validate ingestcfg is available
        const currentIngestcfg = ingestcfg;
        if (!currentIngestcfg) {
          throw new Error('Ingest config not loaded');
        }

        const req = UploadUtils.buildCreateVideoRequest(item, currentIngestcfg);

        // Create backend media record — get S3 key, mediaId, ingestJobId
        const result = await UploadApi.createVideo(capturedCId, req);

        if (!result.dashboard) {
          throw new Error('Backend returned no dashboard ingest data');
        }

        const { key, ingestJobId, data } = result.dashboard;
        const mediaId = data.video?.media.mediaId ?? 0;

        // Update Zustand item with backend response
        useUploadStore.getState().updateItem(item.id, {
          mediaId,
          ingestJobId,
          s3Key: key,
          status: 'creating',
        });

        // Initiate S3 multipart upload
        const contentType = UploadUtils.getContentType(item.filename);
        const multipart = await S3MultipartSigner.createMultipartUpload(
          credentials,
          key,
          contentType
        );

        return { uploadId: multipart.uploadId, key: multipart.key };
      },

      async signPart(file, opts) {
        // CRITICAL for long uploads: credentials can expire mid-upload
        const credentials = await ensureValidCredentials(capturedCId);
        return S3MultipartSigner.signPart(
          credentials,
          opts.key,
          opts.uploadId,
          opts.partNumber
        );
      },

      async listParts(file, opts) {
        const { credentials } = useUploadStore.getState();
        if (!credentials) throw new Error('No credentials available for listParts');
        if (!opts.uploadId) throw new Error('No uploadId for listParts');
        return S3MultipartSigner.listParts(credentials, opts.key, opts.uploadId);
      },

      async completeMultipartUpload(file, opts) {
        const { credentials } = useUploadStore.getState();
        if (!credentials) throw new Error('No credentials available for completeMultipartUpload');
        if (!opts.uploadId) throw new Error('No uploadId for completeMultipartUpload');

        // Map AwsS3Part (optional fields) → required fields for our signer
        const parts = opts.parts
          .filter((p): p is Required<typeof p> => p.ETag != null && p.PartNumber != null)
          .map((p) => ({ ETag: p.ETag!, PartNumber: p.PartNumber! }));

        return S3MultipartSigner.completeMultipartUpload(
          credentials,
          opts.key,
          opts.uploadId,
          parts
        );
      },

      async abortMultipartUpload(file, opts) {
        // Credentials may be null during customer switch — null-guard in S3MultipartSigner
        const { credentials } = useUploadStore.getState();
        if (!opts.uploadId) return; // Nothing to abort without an uploadId
        await S3MultipartSigner.abortMultipartUpload(
          credentials,
          opts.key,
          opts.uploadId
        );
      },
    });

    // --- Wire Uppy events → Zustand store ---

    uppy.on('upload-progress', (file, progress) => {
      if (!file) return;

      const uploadItemId = file.meta?.uploadItemId as string | undefined;
      const { items } = useUploadStore.getState();
      const item = uploadItemId
        ? items.find((i) => i.id === uploadItemId)
        : items.find((i) => i.uppyFileId === file.id);

      if (!item) return;

      const bytesUploaded = progress.bytesUploaded;
      const bytesTotal = progress.bytesTotal ?? item.bytesTotal;
      const uploadProgress = bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0;

      useUploadStore.getState().updateItem(item.id, {
        bytesUploaded,
        bytesTotal,
        uploadProgress,
        status: 'uploading',
        startedAt: item.startedAt ?? Date.now(),
        lastProgressAt: Date.now(),
      });

      // ON FIRST PROGRESS: notify backend that media is ingesting
      if (!item.ingestingNotified && item.mediaId > 0) {
        useUploadStore.getState().updateItem(item.id, { ingestingNotified: true });
        UploadApi.updateStatusToIngesting(capturedCId, item.mediaId).catch(() => {
          // Non-critical — log and continue
        });
      }

      // Throttled progress report to backend
      if (item.ingestJobId > 0 && bytesTotal > 0) {
        const lastTS = lastReportTSRef.current.get(item.id) ?? 0;
        if (Date.now() - lastTS > PROGRESS_REPORT_THROTTLE_MS) {
          lastReportTSRef.current.set(item.id, Date.now());
          const rate = Math.round((bytesUploaded / bytesTotal) * 100);
          UploadApi.updateProgress(capturedCId, item.ingestJobId, rate).catch(() => {
            // Non-critical — log and continue
          });
        }
      }
    });

    uppy.on('upload-success', (file) => {
      if (!file) return;

      const uploadItemId = file.meta?.uploadItemId as string | undefined;
      const { items } = useUploadStore.getState();
      const item = uploadItemId
        ? items.find((i) => i.id === uploadItemId)
        : items.find((i) => i.uppyFileId === file.id);

      if (!item) return;

      useUploadStore.getState().updateItem(item.id, {
        status: 'processing',
        uploadProgress: 100,
        completedAt: Date.now(),
      });

      // Final 100% progress report
      if (item.ingestJobId > 0) {
        UploadApi.updateProgress(capturedCId, item.ingestJobId, 100).catch(() => {});
      }

      // Invalidate video list to show new video
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(capturedCId) });
    });

    uppy.on('upload-error', (file, error) => {
      if (!file) return;

      const uploadItemId = file.meta?.uploadItemId as string | undefined;
      const { items } = useUploadStore.getState();
      const item = uploadItemId
        ? items.find((i) => i.id === uploadItemId)
        : items.find((i) => i.uppyFileId === file.id);

      if (!item) return;

      // Conservative error handling (matches legacy pattern):
      // If no bytes sent → mark as error immediately
      // If some bytes sent → keep 'uploading', let job polling determine outcome
      if (item.bytesUploaded === 0) {
        useUploadStore.getState().updateItem(item.id, {
          status: 'error',
          error: error?.message ?? 'Upload failed',
        });
      }
    });

    uppy.on('restriction-failed', (_file, error) => {
      notifications.show({
        title: 'File rejected',
        message: error?.message ?? 'File does not meet upload requirements',
        color: 'red',
      });
    });

    return uppy;
  }, [cId, ingestcfg, queryClient]);

  // Lifecycle: recreate Uppy when cId changes
  useEffect(() => {
    const instance = createUppyInstance();
    uppyRef.current = instance;
    lastReportTSRef.current.clear();

    return () => {
      if (instance) {
        instance.cancelAll();
        instance.destroy();
      }
    };
  }, [createUppyInstance]);

  const addFilesWithMeta = useCallback(
    (filePairs: Array<{ file: File; uploadItemId: string }>) => {
      const uppy = uppyRef.current;
      if (!uppy) return;

      for (const { file, uploadItemId } of filePairs) {
        try {
          const uppyFileId = uppy.addFile({
            name: file.name,
            type: file.type || UploadUtils.getContentType(file.name),
            data: file,
            meta: { uploadItemId },
          });

          // Backlink Uppy file ID to UploadItem
          useUploadStore.getState().updateItem(uploadItemId, { uppyFileId });
        } catch {
          // Uppy throws on duplicate files (same name+size+type)
          // restriction-failed event handles the notification
        }
      }
    },
    []
  );

  const cancelUpload = useCallback((uppyFileId: string) => {
    const uppy = uppyRef.current;
    if (!uppy) return;
    uppy.removeFile(uppyFileId);
  }, []);

  const cancelAll = useCallback(() => {
    const uppy = uppyRef.current;
    if (!uppy) return;
    uppy.cancelAll();
  }, []);

  const retryUpload = useCallback((uppyFileId: string) => {
    const uppy = uppyRef.current;
    if (!uppy) return;
    uppy.retryUpload(uppyFileId);
  }, []);

  return { addFilesWithMeta, cancelUpload, cancelAll, retryUpload };
}
